import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import { Booking } from '../models/booking.model';
import { BookingStatus } from '../enums/booking-status.enum';
import { PaymentStatus } from '../enums/payment-status.enum';
import { normalizeApiArray } from '../utils/api-response.util';

export interface BookingStats {
  totalBookings: number;
  confirmedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  todayBookings: number;
  monthlyBookings: number;
  upcomingBookings?: number;
  pastBookings?: number;
  upcomingTrips?: number;
  pastTrips?: number;
  monthlyRevenue: number;
}

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private apiUrl = `${environment.apiUrl}/Booking`;

  constructor(private http: HttpClient) { }

  // Get all bookings (with optional filters)
  getBookings(filters?: {
    status?: BookingStatus;
    startDate?: string;
    endDate?: string;
    tripId?: number;
    userId?: number;
  }): Observable<Booking[]> {
    let params = new HttpParams();
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters];
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }
    
    return this.http.get<unknown>(this.apiUrl, { params }).pipe(
      map(response => normalizeApiArray<Booking>(response).map(booking => this.normalizeBooking(booking)))
    );
  }

  // Get booking by ID
  getBookingById(id: number): Observable<Booking> {
    return this.http.get<Booking>(`${this.apiUrl}/${id}`).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  getBookingByReference(bookingReference: string): Observable<Booking> {
    return this.http.get<Booking>(`${this.apiUrl}/reference/${encodeURIComponent(bookingReference)}`).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Create new booking
  createBooking(bookingData: Partial<Booking>): Observable<Booking> {
    return this.http.post<Booking>(this.apiUrl, bookingData).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Update booking
  updateBooking(id: number, bookingData: Partial<Booking>): Observable<Booking> {
    return this.http.put<Booking>(`${this.apiUrl}/${id}`, bookingData).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Update booking status
  updateBookingStatus(id: number, status: BookingStatus): Observable<Booking> {
    return this.http.patch<Booking>(`${this.apiUrl}/${id}/status`, { status }).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Update payment status
  updatePaymentStatus(id: number, paymentStatus: PaymentStatus): Observable<Booking> {
    return this.http.patch<Booking>(`${this.apiUrl}/${id}/payment`, { paymentStatus }).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Cancel booking
  cancelBooking(id: number, reason?: string): Observable<Booking> {
    return this.http.delete<Booking>(`${this.apiUrl}/${id}`, {
      body: { reason }
    }).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Legacy compatibility only. Passenger screens should use getBookings() so the API scopes by token.
  getUserBookings(userId: number): Observable<Booking[]> {
    return this.getBookings({ userId });
  }

  // Get bookings for a specific trip
  getTripBookings(tripId: number): Observable<Booking[]> {
    return this.http.get<unknown>(`${this.apiUrl}/trip/${tripId}`).pipe(
      map(response => normalizeApiArray<Booking>(response).map(booking => this.normalizeBooking(booking)))
    );
  }

  cancelBookingById(id: number, reason?: string): Observable<Booking> {
    return this.http.put<Booking>(`${this.apiUrl}/cancel/${id}`, { reason }).pipe(
      map(booking => this.normalizeBooking(booking))
    );
  }

  // Get booking statistics
  getBookingStats(): Observable<BookingStats> {
    return this.http.get<BookingStats>(`${this.apiUrl}/stats`);
  }

  private normalizeBooking(booking: Booking): Booking {
    const bookingStatus = this.resolveBookingStatus(booking);
    const tripName = booking.tripName || booking.trip?.tripName || '';
    const tripStart = booking.tripStartTime || booking.trip?.tripStartDateTime || booking.trip?.tripStartTime || '';
    const tripEnd = booking.tripEndTime || booking.trip?.tripEndDateTime || booking.trip?.tripEndTime || '';

    return {
      ...booking,
      bookingStatus,
      numberOfSeats: booking.numberOfSeats ?? booking.seatsBooked ?? 0,
      seatsBooked: booking.seatsBooked ?? booking.numberOfSeats ?? 0,
      seatNumbers: this.normalizeSeatNumbers(booking.seatNumbers),
      totalPrice: Number(booking.totalPrice || 0),
      paymentStatus: this.resolvePaymentStatus(booking.paymentStatus),
      tripName,
      tripStartTime: tripStart,
      tripEndTime: tripEnd,
      trip: booking.trip || {
        tripID: booking.tripID,
        tripName,
        tripStartDateTime: tripStart,
        tripEndDateTime: tripEnd
      } as any
    };
  }

  private resolveBookingStatus(booking: Booking): BookingStatus {
    const statusName = String(booking.bookingStatus || booking.statusName || booking.status?.statusName || '').toLowerCase();

    if (booking.statusID === 4 || statusName === 'pending') {
      return BookingStatus.PENDING;
    }

    if (booking.statusID === 6 || statusName === 'cancelled' || statusName === 'canceled') {
      return BookingStatus.CANCELLED;
    }

    return BookingStatus.CONFIRMED;
  }

  private resolvePaymentStatus(status: PaymentStatus | string | undefined): PaymentStatus | string {
    const normalized = String(status || '').toLowerCase();

    if (!normalized) {
      return PaymentStatus.PENDING;
    }

    if (normalized === 'payatticketoffice' || normalized === 'pay_at_ticket_office') {
      return 'PayAtTicketOffice';
    }

    return status || PaymentStatus.PENDING;
  }

  private normalizeSeatNumbers(value: unknown): number[] {
    const source = Array.isArray(value)
      ? value
      : Array.isArray((value as { $values?: unknown[] } | null | undefined)?.$values)
        ? (value as { $values: unknown[] }).$values
        : [];

    return source.map(Number).filter(seat => Number.isFinite(seat) && seat > 0);
  }
}
